import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useStorefront } from "../hooks/useStorefront";
import { useStoreProducts } from "../hooks/useStoreProducts";
import StorefrontAbout from "../storefront/StorefrontAbout";
import StorefrontGrid from "../storefront/StorefrontGrid";
import StorefrontHero from "../storefront/StorefrontHero";
import StorefrontRedirectNotice from "../storefront/StorefrontRedirectNotice";
import StorefrontShell from "../storefront/StorefrontShell";

const resolveIdentifier = (storeSlug) => {
  // The backend can resolve either a slug or a custom domain identifier.
  if (storeSlug) {
    return storeSlug;
  }

  return window.location.hostname;
};

const Storefront = () => {
  const { storeSlug } = useParams();
  const identifier = resolveIdentifier(storeSlug);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeMarketplace, setActiveMarketplace] = useState("all");
  const [wishlist, setWishlist] = useState(new Set());

  const {
    data: config,
    isLoading: configLoading,
    isError: configError,
    error: configErrorDetails,
  } = useStorefront(identifier);

  const {
    data: productsData,
    isLoading: productsLoading,
    isError: productsError,
  } = useStoreProducts(identifier);

  const products = useMemo(
    () => productsData?.products ?? [],
    [productsData?.products]
  );

  const marketplaces = useMemo(() => {
    return Array.from(new Set(products.map((product) => product.marketplace)));
  }, [products]);

  useEffect(() => {
    if (activeMarketplace !== "all" && !marketplaces.includes(activeMarketplace)) {
      setActiveMarketplace("all");
    }
  }, [activeMarketplace, marketplaces]);

  const filteredProducts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return products.filter((product) => {
      const matchesMarketplace =
        activeMarketplace === "all" || product.marketplace === activeMarketplace;
      const matchesQuery =
        normalizedQuery.length === 0 ||
        product.title.toLowerCase().includes(normalizedQuery);

      return matchesMarketplace && matchesQuery;
    });
  }, [products, searchQuery, activeMarketplace]);

  const featuredProducts = useMemo(() => {
    if (!config?.features.enableFeatured) {
      return [];
    }

    return filteredProducts.slice(0, 4);
  }, [config, filteredProducts]);

  const visibleProducts = useMemo(() => {
    if (!config?.features.enableFeatured) {
      return filteredProducts;
    }

    const featuredIds = new Set(featuredProducts.map((product) => product.id));
    return filteredProducts.filter((product) => !featuredIds.has(product.id));
  }, [config, filteredProducts, featuredProducts]);

  const handleWishlistToggle = (productId) => {
    setWishlist((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  if (configLoading) {
    return <div className="storefront-state">Loading storefront...</div>;
  }

  if (configError || !config) {
    return (
      <div className="storefront-state storefront-state--error">
        Unable to load this storefront.
        {configErrorDetails ? ` ${configErrorDetails.message}` : ""}
      </div>
    );
  }

  return (
    <StorefrontShell config={config}>
      <StorefrontHero config={config} />
      <StorefrontGrid
        config={config}
        products={visibleProducts}
        featuredProducts={featuredProducts}
        isLoading={productsLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        marketplaces={marketplaces}
        activeMarketplace={activeMarketplace}
        onMarketplaceChange={setActiveMarketplace}
        wishlist={wishlist}
        onWishlistToggle={handleWishlistToggle}
      />
      {productsError ? (
        <div className="storefront-state storefront-state--error">
          Listings are unavailable right now.
        </div>
      ) : null}
      <StorefrontRedirectNotice config={config} />
      {config.layout.showAboutSection ? <StorefrontAbout config={config} /> : null}
    </StorefrontShell>
  );
};

export default Storefront;
